import React, { useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer,
} from 'recharts';
import { format, parseISO, startOfMonth, isValid } from 'date-fns';

function getMonthKey(dateStr) {
  try {
    const d = parseISO(dateStr);
    return isValid(d) ? format(startOfMonth(d), 'yyyy-MM') : null;
  } catch { return null; }
}

export default function RevenueChart({ data }) {
  const chartData = useMemo(() => {
    const map = {};
    data.forEach(inv => {
      if (inv.source === 'ghl') return;
      // effectiveDate is the field /revenue/combined actually sets on every item (job.completed_on
      // for job invoices, invoice_date for standalone) - job invoices have no invoiceDate/createdOn
      // at all, so without checking effectiveDate first, all job revenue (the majority of real
      // ST revenue) silently got key=null and was dropped from this chart entirely.
      const key = getMonthKey(inv.effectiveDate || inv.invoiceDate || inv.createdOn || inv.issueDate || inv.dateAdded || inv.createdAt || inv.date);
      if (!key) return;
      if (!map[key]) map[key] = { month: key, servicetitan: 0, manual: 0 };
      const amt = inv.total || inv.amount || 0;
      const src = inv.source || 'manual';
      if (src === 'servicetitan') map[key].servicetitan += amt;
      else map[key].manual += amt;
    });
    return Object.values(map)
      .sort((a, b) => a.month.localeCompare(b.month))
      .map(d => ({ ...d, month: format(parseISO(d.month + '-01'), 'MMM yy') }));
  }, [data]);

  const fmt = (v) => `$${(v / 1000).toFixed(0)}k`;

  return (
    <ResponsiveContainer width="100%" height={280}>
      <AreaChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
        <defs>
          <linearGradient id="stGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="manualGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
        <YAxis tickFormatter={fmt} tick={{ fontSize: 12 }} />
        <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, undefined]} />
        <Legend />
        <Area type="monotone" dataKey="servicetitan" name="ServiceTitan" stroke="#f59e0b" fill="url(#stGrad)" strokeWidth={2} />
        <Area type="monotone" dataKey="manual" name="Manual" stroke="#22c55e" fill="url(#manualGrad)" strokeWidth={2} />
      </AreaChart>
    </ResponsiveContainer>
  );
}
